import React, { useEffect, useRef, useState } from 'react';
import { Tabulator, FormatModule, DownloadModule, PageModule, ExportModule } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";
import { MultiSelect } from './MultiSelect';
import { MultiSelectAsync } from './MultiSelectAsync';
import 'bootstrap-select/dist/css/bootstrap-select.min.css';
import 'bootstrap-select';
import * as d3 from "d3";
import { VendorHighestPaidFilters } from 'types/employee';
import { Button, Card, Col, Collapse, Row } from 'react-bootstrap';
import EntityVendorHighestPaidTable from './EntityVendorHighestPaidTable';

Tabulator.registerModule([FormatModule, DownloadModule, PageModule, ExportModule]);

interface HighestPaidVendors {
  fiscal_year: number;
  entity_name: string;
  vendor_name: string;
  vendor_beg_name_id: number;
  net_amount: number;
}

interface HighestPaidVendorProps {
  highestPaidVendorData: HighestPaidVendors[];
  parentEntityName?: string;
  parentEntityId?: number | null;
  formatCurrency: (value: number) => string;
  formatEntityString: (value: string) => string;
}

const EntityVendorHighestPaidOverview: React.FC<HighestPaidVendorProps> = ({
  highestPaidVendorData,
  parentEntityName,
  parentEntityId,
  formatCurrency,
  formatEntityString,
}) => {

  const [data, setData] = useState<HighestPaidVendors[]>([]);
  const [filteredData, setFilteredData] = useState<HighestPaidVendors[]>([]);

  const [filters, setFilters] = useState<VendorHighestPaidFilters>({
    filterYear: [],
    filterEntity: [],
    filterVendor: [],
  });

  const [selectedFilteredYears, setSelectedFilteredYears] = useState<string[]>([]);
  const [selectedFilteredVendors, setSelectedFilteredVendors] = useState<string[]>([]);
  const [options, setOptions] = useState<string[]>([]);
  const [showHeaderInfo, setShowHeaderInfo] = useState<boolean>(false);

  const tableRef = useRef<HTMLDivElement>(null);
  const tabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);

  const toTitleCaseLocal = (str: string) =>
  String(str ?? "").toLowerCase().replace(/\b\w/g, c => c.toUpperCase()).trim();

  const formatTextForFileDownload = (entityName: string) => {
    const formattedEntityName = entityName?.toLowerCase()?.replaceAll(" ", "_") || "Entity";
    return formattedEntityName;
  }

  useEffect(() => {
    if (!highestPaidVendorData?.length) return;

    setData(highestPaidVendorData);
    setFilteredData(highestPaidVendorData);

    const years = Array.from(
      new Set(highestPaidVendorData.map(d => d.fiscal_year))
    ).sort((a, b) => b - a);

    const vendors = Array.from(
      new Set(highestPaidVendorData.map(d => d.vendor_name))
    ).sort();

    setFilters(prev => ({
      ...prev,
      filterYear: years,
      filterVendor: vendors,
    }));

    // Clear any previous selected filter values for fiscal year or vendor
    setSelectedFilteredYears([]);
    setSelectedFilteredVendors([]);
  }, [highestPaidVendorData]);

  // Updated version - Set and update filtered data
  useEffect(() => {
    let temp = [...data];

    if (selectedFilteredYears.length) {
      temp = temp.filter(d =>
        selectedFilteredYears.includes(d.fiscal_year.toString())
      );
    }

    if (selectedFilteredVendors.length) {
      temp = temp.filter(d =>
        selectedFilteredVendors.includes(toTitleCaseLocal(d.vendor_name))
      );
    }

    setFilteredData(temp);
  }, [selectedFilteredYears, selectedFilteredVendors, data]);

  // Set options for async multiselect form
  useEffect(() => {
    const vendors = Array.from(new Set(filteredData.map(vendor => vendor.vendor_name).sort()));
    const titleCaseVendors = vendors.map(str => 
      str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase())
    );
    setOptions(titleCaseVendors); 
  }, [filteredData])

  const handleDownload = (type: "csv" | "xlsx" | "html" | "json") => {
    if (!tabulatorRef.current) {
      return;
    }

    switch (type) {
      case "csv":
        tabulatorRef.current.download("csv", `${formatTextForFileDownload(parentEntityName ?? "entity")}_vendor_payments.csv`);
        break;
      case "xlsx":
        tabulatorRef.current.download("xlsx", `${formatTextForFileDownload(parentEntityName ?? "entity")}_vendor_payments.xslx`);
        break;
      case "html":
        tabulatorRef.current.download("html", `${formatTextForFileDownload(parentEntityName ?? "entity")}_vendor_payments.html`, { style: true });
        break;
      case "json":
        tabulatorRef.current.download("json", `${formatTextForFileDownload(parentEntityName ?? "entity")}_vendor_payments.json`);
        break;
    }
  };

  const HighestPaidVendorsByEntityChart : React.FC<{ data: HighestPaidVendors[] }> = ({ data }) => {
    const chartRef = useRef<SVGSVGElement | null>(null);

    // Download D3 current chart as PNG
    const handleD3ChartDownload = (svgNode: SVGSVGElement) => {
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svgNode);
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        if (context) {
          context.fillStyle = "white";
          context.fillRect(0, 0, canvas.width, canvas.height);

          context.drawImage(img, 0, 0);
        }
        URL.revokeObjectURL(url);

        const pngUrl = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = pngUrl;
        a.download = 'chart.png';
        a.click();
      };
      img.src = url;
    };

    useEffect(() => {
      if (!data.length) return;

      const svg = d3.select(chartRef.current);
      const container = chartRef.current?.parentElement;
      if (!container) return;

      const renderChart = () => {
        if (!chartRef.current || !container) return;

        const containerWidth = container.offsetWidth;
        const margin = { top: 30, right: 30, bottom: 75, left: 80 };
        const width = containerWidth - margin.left - margin.right;
        const height = 400 - margin.top - margin.bottom;

        // Clear previous render
        svg.selectAll("*").remove();
        d3.select(container).selectAll(".tooltip").remove();

        svg
          .attr("viewBox", `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
          .attr("preserveAspectRatio", "xMidYMid meet");

        const g = svg
          .append("g")
          .attr("transform", `translate(${margin.left},${margin.top})`);

        // Aggregate data by fiscal year
        const aggregated = data.reduce((res: Record<string, any>, d) => {
          if (!res[d.fiscal_year]) {
            res[d.fiscal_year] = { fiscal_year: d.fiscal_year, net_amount: 0 };
          }
          res[d.fiscal_year].net_amount += Number(d.net_amount) || 0;
          return res;
        }, {});

        // Convert to array for D3
        const dataByYear = Object.values(aggregated);

        dataByYear.forEach((d: any) => {
          d.total = d.net_amount;
        });

        const keys: string[] = ["net_amount"];

        // Scales
        const x = d3.scaleBand<string>()
          .domain(dataByYear.map(d => d.fiscal_year))
          .range([0, width])
          .padding(0.1);

        const y = d3.scaleLinear()
          .domain([0, d3.max(dataByYear, d => d.total) ?? 0])
          .range([height, 0]);

        const z = d3.scaleOrdinal<string>()
          .domain(keys)
          .range(["#2E8B8B"]);

        // Tooltip
        const tooltip = d3.select(container)
          .append("div")
          .attr("class", "tooltip")
          .style("position", "absolute")
          .style("visibility", "hidden")
          .style("background", "white")
          .style("border", "1px solid gray")
          .style("padding", "5px")
          .style("border-radius", "4px")
          .style("pointer-events", "none")
          .style("font-size", "12px")
          .style("z-index", "1000");

        // Legend
        const legend = svg.append("g")
          .attr("class", "legend")
          .attr("transform", `translate(${margin.left + 5},${margin.top})`);

        legend.append("text")
          .attr("transform", "rotate(-90)")
          .attr("x", -130)
          .attr("y", -85)
          .attr("dy", "1em")
          .style("text-anchor", "middle")
          .style("font-size", "12px")
          .text("Dollars($)");

        legend.selectAll("rect")
          .data(keys)
          .enter()
          .append("rect")
          .attr("x", (d, i) => i * 100)
          .attr("y", 0)
          .attr("width", 15)
          .attr("height", 15)
          .attr("fill", d => z(d) as string);

        legend.selectAll("text.label")
          .data(keys)
          .enter()
          .append("text")
          .attr("class", "label")
          .attr("x", (d, i) => i * 100 + 20)
          .attr("y", 12)
          .text(d => d.replace("_", " ").toLowerCase().replace(/\b\w/g, char => char.toUpperCase()))
          .style("font-size", "12px")
          .attr("fill", "#000");

        // Stack generator
        const stackGenerator = d3.stack<YearData>().keys(keys);
        const stackSeries = stackGenerator(dataByYear);

        g.selectAll("g.layer")
          .data(stackSeries)
          .join("g")
          .attr("class", "layer")
          .attr("fill", d => z(d.key) as string)
          .selectAll("rect")
          .data(d => d)
          .join("rect")
          .attr("x", (d: any) => x(d.data.fiscal_year) ?? 0)
          .attr("y", (d: any) => y(d[1]))
          .attr("height", (d: any) => y(d[0]) - y(d[1]))
          .attr("width", x.bandwidth())
          .on("mouseover", (event: any, d: any) => {
            const parentDatum = d3.select<SVGGElement, any>(event.currentTarget.parentNode as SVGGElement).datum() as { key: string };
            const formattedParentDatum = parentDatum.key
              .replace("_", " ")
              .toLowerCase()
              .replace(/\b\w/g, (char: string) => char.toUpperCase());
            tooltip
              .style("visibility", "visible")
              .style("opacity", "1")
              .html(`
                <strong>${d.data.fiscal_year}</strong><br/>
                ${formattedParentDatum}: $${(d[1] - d[0]).toLocaleString()}
              `);
          })
          .on("mousemove", (event: any) => {
            const [x, y] = d3.pointer(event, container);
            tooltip.style("top", `${y - 30}px`).style("left", `${x + 10}px`);
          })
          .on("mouseout", () => {
            tooltip.style("visibility", "hidden");
          });

        // Axes
        g.append("g")
          .call(
            d3.axisLeft(y)
              .tickFormat(d3.format("$,~"))
          )
        g.append("g")
          .attr("transform", `translate(0,${height})`)
          .call(d3.axisBottom(x));
      };

      renderChart();

      const resizeObserver = new ResizeObserver(() => renderChart());
      if (chartRef.current?.parentElement) resizeObserver.observe(chartRef.current.parentElement);

      return () => resizeObserver.disconnect();
    }, [data]);

    return (
      <div id="chart-container" style={{ position: "relative" }}>
        <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
          <div
            className="btn btn-secondary"
            id="btn-chart-download"
            onClick={() => chartRef.current && handleD3ChartDownload(chartRef.current)}
            style={{ margin: 0 }}
          >
            Download
          </div>

          <div
            className="btn btn-secondary"
            id="btn-chart-fullscreen"
            data-toggle="modal"
            data-target="#fullscreen-modal"
            style={{ margin: 0 }}
          >
            <i className="fas fa-expand-arrows-alt"></i>
          </div>
        </div>

        {/* Chart SVG */}
        <svg ref={chartRef} style={{ width: "100%", height: "400px" }} />
      </div>
    );
  }

  return (
    <div className="entity-vendor-highest-paid">
      <Row className="mb-2">
        <Col xs={12}>
          <Card bg="secondary" text="white" style={{ border: "none", borderRadius: "0" }}>
            <Card.Body style={{ justifyItems: "center" }}>
              <div className="tu_tabHeader">
                <div className="tu-tabHeader__titleRow">
                  <h1 className="tu-tabHeader__title">
                    Vendor Payments</h1>
                    <button
                      type="button"
                      className="tu-tabHeader__infoBtn"
                      onClick={() => setShowHeaderInfo(!showHeaderInfo)}
                      aria-label="Vendor payment info"
                    >
                      <i className="fas fa-info"/>
                    </button>
                </div>
              </div>

              <Collapse in={showHeaderInfo}>
                  <div>
                    <p className="lead mt-2 text-center">
                      The vendor payments tab allows you to view the vendors used by the entity. You can filter by year or name of the vendor. The data is available in a table format and graph format. Please note that the vendor name filter will return partial matches on a reported vendor name. Different government entities may use different names for what seems to be the same vendor. "Office Depot/Office Max" may include the city name, the store number, or just one of the two names.
                    </p>
                  </div>
                </Collapse>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12}>
          <Card>
            <Card.Header style={{ backgroundColor: "var(--tu-teal)" }}>
              <Card.Title className="text-white" as="h2" style={{ fontSize: "1.5rem", textTransform: "none" }}>Filter</Card.Title>
            </Card.Header>

            <Card.Body className="pt-3">
              <Row>
                {/* Fiscal Year */}
                <Col xs={12} md={6} className="mb-3">
                  <h5>
                    <i className="far fa-calendar-alt"></i>&nbsp;Filter Year
                  </h5>
                  <MultiSelect
                    options={filters.filterYear.map(y => ({
                      value: y.toString(),
                      label: y.toString(),
                    }))}
                    value={selectedFilteredYears.map(y => ({
                      value: y,
                      label: y,
                    }))}
                    onChange={opts =>
                      setSelectedFilteredYears(
                        opts ? opts.map(o => o.value) : []
                      )
                    }
                    isSearchable
                    closeMenuOnSelect={false}
                  />
                </Col>

                {/* Vendor */}
                <Col xs={12} md={6}>
                  <h5>
                    <i className="fas fa-people-carry"></i>&nbsp;Filter Vendor
                  </h5>

                  {/* Updated Multiselect async implementation */}
                  <MultiSelectAsync
                    options={options.map(o => ({ value: o, label: o }))}
                    value={selectedFilteredVendors.map(v => ({ value: v, label: v }))}
                    onChange={opts =>
                      setSelectedFilteredVendors(opts ? opts.map(o => o.value) : [])
                    }
                    isSearchable
                    loadOptions={async (inputValue: string) => {
                      return options
                        .filter(o => o.toLowerCase().includes(inputValue.toLowerCase()))
                        .map(o => ({ value: o, label: o }));
                    }}
                    defaultOptions={options.map(o => ({ value: o, label: o }))}
                    closeMenuOnSelect={false}
                  />
                  
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Result Section */}
      {filteredData && filteredData.length > 0 && (
        <Row className="py-4 px-4">
          <Col xs={12}>
            <Card className="bg-white">
              <Card.Header className="text-white" style={{ backgroundColor: "var(--tu-teal)"}}>
                <Card.Title className="text-white" as="h2" style={{ fontSize: "1.5rem", textTransform: "none" }}>Data</Card.Title>
              </Card.Header>
              <Card.Body>
                <div id="data-download-buttons-container" className="col-12">
                  <button id="download-csv" className="btn btn-secondary mb-2 mt-2" onClick={() => handleDownload("csv")}>CSV</button>
                  <button id="download-xlsx" className="btn btn-secondary mb-2 mt-2" onClick={() => handleDownload("xlsx")}>Excel</button>
                  <button id="download-html" className="btn btn-secondary mb-2 mt-2" onClick={() => handleDownload("html")}>HTML</button>
                  <button id="download-json" onClick={() => handleDownload("json")} className="btn btn-secondary mb-2 mt-2">JSON</button>
                </div>

              <Col xs={12}>
                <div id="tabulator-table" className="table-light">
                  <EntityVendorHighestPaidTable ref={tabulatorRef} data={filteredData}/>
                </div>
              </Col>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Chart Section */}
      {filteredData && filteredData.length > 0 && (
        <div className="row py-4 px-4">
          <div className="col-12">
            <div className="card bg-white">
              {/* header */}
              <div
                className="card-header text-white"
                style={{ backgroundColor: "var(--tu-teal)" }}
              >
                <Card.Title className="text-white" as="h2" style={{ fontSize: "1.5rem", textTransform: "none" }}>Chart</Card.Title>
              </div>

              {/* body */}
              <div className="card-body">
                <section>
                  <div className="col-12" id="chart-container">
                    <div className="chart-spinner" id="chart-container-spinner" />
                    <div id="emp-comp-chart"></div>
                  </div>

                  {/* Chart comp */}
                  <HighestPaidVendorsByEntityChart data={filteredData} />
                </section>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EntityVendorHighestPaidOverview;